import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import Select, { SelectOption } from 'components/UI/V3/Forms/Select';
import { Spinner } from 'components/UI';
import styles from './CancellationNoShowsChart.module.scss';

import type { CancellationAndNoShowsReport, CancellationAndNoShowsRow } from 'model/CancellationAndNoShowsReport';

type Slice = {
    name: string;
    value: number;
    percentage: number;
    color: string;
};

type MetricKey = 'totalBookings' | 'totalValue' | 'depositCollected' | 'missedRevenue' | 'rebooked' | 'notRebooked';

const STATUS_META: Record<string, { label: string; color: string }> = {
    '1': { label: 'Mistake or Error', color: '#5E8677' },
    '2': { label: 'Staff canceled', color: '#92B4A7' },
    '3': { label: 'Client canceled', color: '#BAA997' },
    '5': { label: 'Pet Decline', color: '#6C6C6C' },
    '4': { label: 'No Show', color: '#EAB464' },
    '6': { label: 'No Reason', color: '#B2B2B2' }
};

const METRIC_OPTIONS: Array<SelectOption<null>> = [
    { label: 'Total Bookings', value: 'totalBookings' },
    { label: 'Total Value', value: 'totalValue' },
    { label: 'Deposit Collected', value: 'depositCollected' },
    { label: 'Missed Revenue', value: 'missedRevenue' },
    { label: 'Rebooked', value: 'rebooked' },
    { label: 'Not Rebooked', value: 'notRebooked' }
];

interface Props {
    loading: boolean;
    report: CancellationAndNoShowsReport;
}

const money = (n: number) =>
    n.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0
    });

const getMetricValue = (metric: MetricKey, row: CancellationAndNoShowsRow): number => {
    switch (metric) {
        case 'totalBookings':
            return Number(row.totalBookings ?? 0);
        case 'totalValue':
            return Number(row.totalValue ?? 0);
        case 'depositCollected':
            return Number(row.depositCollected ?? 0);
        case 'missedRevenue':
            return Number(row.missedRevenue ?? 0);
        case 'rebooked':
            return Number(row.rebooked?.value ?? 0);
        case 'notRebooked':
            return Number(row.notRebooked?.value ?? 0);
        default:
            return 0;
    }
};

const formatLegendValue = (metric: MetricKey, row: CancellationAndNoShowsRow) => {
    const raw = getMetricValue(metric, row);

    if (metric === 'totalValue' || metric === 'depositCollected' || metric === 'missedRevenue') {
        return money(raw);
    }
    return `${raw}`;
};

export const CancellationNoShowsChart: React.FC<Props> = ({ loading, report }) => {
    const [metric, setMetric] = React.useState<MetricKey>('totalBookings');

    const rows = report?.rows ?? [];

    const rowsWithoutTotal = React.useMemo(() => rows.filter(r => String(r.statusId) !== 'total'), [rows]);

    const totalRow = React.useMemo(() => rows.find(r => String(r.statusId) === 'total'), [rows]);

    const data: Slice[] = React.useMemo(() => {
        const denom = rowsWithoutTotal.reduce((s, r) => s + getMetricValue(metric, r), 0);

        return rowsWithoutTotal.map(r => {
            const id = String(r.statusId);
            const meta = STATUS_META[id] ?? { label: `Status ${id}`, color: '#D5E1DB' };

            const v = getMetricValue(metric, r);
            const pct = denom > 0 ? (v * 100) / denom : 0;

            return {
                name: meta.label,
                value: v,
                percentage: Number(pct.toFixed(0)),
                color: meta.color
            };
        });
    }, [rowsWithoutTotal, metric]);

    const centerTotalValue = React.useMemo(() => {
        if (totalRow) return getMetricValue(metric, totalRow);
        return rowsWithoutTotal.reduce((s, r) => s + getMetricValue(metric, r), 0);
    }, [totalRow, rowsWithoutTotal, metric]);

    const centerTotalLabel = React.useMemo(() => {
        if (metric === 'totalValue' || metric === 'depositCollected' || metric === 'missedRevenue') {
            return money(centerTotalValue);
        }
        return `${centerTotalValue}`;
    }, [metric, centerTotalValue]);

    return (
        <div className={styles.cancellationNoShows}>
            <div className={styles.card}>
                <div className={styles.headerRow}>
                    <h3 className={styles.title}>Cancellation & No Shows</h3>

                    <Select
                        className={styles.inlineSelect}
                        options={METRIC_OPTIONS}
                        value={metric}
                        onChange={val => setMetric(val as MetricKey)}
                        placeholder="Select metric"
                    />
                </div>

                <div className={styles.content}>
                    {loading ? (
                        <div className={styles.loading}>
                            <Spinner className={styles.spinner} />
                        </div>
                    ) : (
                        <>
                            <div className={styles.pieWrap}>
                                <ResponsiveContainer
                                    width="100%"
                                    height={220}
                                >
                                    <PieChart>
                                        <Pie
                                            data={data}
                                            dataKey="value"
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={70}
                                            outerRadius={100}
                                            startAngle={90}
                                            endAngle={450}
                                            stroke="none"
                                        >
                                            {data.map((s, i) => (
                                                <Cell
                                                    key={i}
                                                    fill={s.color}
                                                />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>

                                <div className={styles.centerLabel}>
                                    <div className={styles.totalValue}>{centerTotalLabel}</div>
                                    <div className={styles.totalPercentage}>100%</div>
                                </div>
                            </div>

                            <div className={styles.legend}>
                                {rowsWithoutTotal.map((row, i) => {
                                    const id = String(row.statusId);
                                    const meta = STATUS_META[id] ?? { label: `Status ${id}`, color: '#D5E1DB' };

                                    // % lo tomamos del data (mismo orden por map)
                                    const pct = data[i]?.percentage ?? 0;

                                    return (
                                        <div
                                            key={id}
                                            className={styles.legendItem}
                                        >
                                            <div
                                                className={styles.legendDot}
                                                style={{ backgroundColor: meta.color }}
                                            />
                                            <span className={styles.legendLabel}>{meta.label}</span>
                                            <span className={styles.legendValue}>
                                                {formatLegendValue(metric, row)} ({pct}%)
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CancellationNoShowsChart;
